'use client';

import { useCallback, useState } from 'react';

import { useRouter } from 'next/navigation';

import { buildPaymentDescription } from '@kit/payments/description';

import { HyperpayCheckoutDialog } from '../../book/_components/hyperpay-checkout-dialog';
import { MoyasarCheckoutDialog } from '../../book/_components/moyasar-checkout-dialog';
import { PackageCheckoutDialog } from '../../book/_components/package-checkout-dialog';
import { StripeCheckoutForm } from '../../book/_components/stripe-checkout-form';
import type {
  HyperpayCheckoutData,
  MoyasarCheckoutData,
} from '../../book/_lib/hooks/use-booking-reducer';
import { usePackagePurchase } from '../../book/_lib/hooks/use-package-purchase';
import type { PublicDiscountCode } from '../../book/_lib/public-discount-codes';
import type { AvailablePackage } from '../_lib/server/available-packages.loader';

interface PackagePurchaseFlowProps {
  /** The package being bought; null closes the flow. */
  pkg: AvailablePackage | null;
  onClose: () => void;
  userId?: string;
  userName?: string;
  userPhone?: string;
  tenantId?: string;
  tenantSlug?: string | null;
  publishableKey?: string | null;
  provider?: string | null;
  publicDiscountCodes: PublicDiscountCode[];
  walletEnabled: boolean;
  walletBalance: number;
  onWalletBalanceChange: (balance: number) => void;
}

/**
 * Buys a package without leaving /packages.
 *
 * Previously the catalog deep-linked to `/book?package=<id>`, which dropped the
 * customer on a different bottom-tab: cancelling the dialog stranded them on
 * Book rather than returning them to Packages, so Back no longer matched what
 * they had done.
 *
 * All the payment branching lives in usePackagePurchase(), shared with /book —
 * this component only owns which dialog is open. Provider dialogs are mounted
 * here (rather than driven by /book's reducer) with plain local state.
 */
export function PackagePurchaseFlow({
  pkg,
  onClose,
  userId,
  userName,
  userPhone,
  tenantId,
  tenantSlug,
  publishableKey,
  provider,
  publicDiscountCodes,
  walletEnabled,
  walletBalance,
  onWalletBalanceChange,
}: PackagePurchaseFlowProps) {
  const router = useRouter();
  const [hyperpay, setHyperpay] = useState<HyperpayCheckoutData | null>(null);
  const [moyasar, setMoyasar] = useState<MoyasarCheckoutData | null>(null);
  const [stripeSecret, setStripeSecret] = useState<string | null>(null);

  const { purchase, purchaseWithWallet, isProcessing } = usePackagePurchase({
    onCloseCheckoutDialog: onClose,
    onHyperpay: useCallback(
      (data: HyperpayCheckoutData) => setHyperpay(data),
      [],
    ),
    onStripe: useCallback((secret: string) => setStripeSecret(secret), []),
    onMoyasar: useCallback((data: MoyasarCheckoutData) => setMoyasar(data), []),
    // Unlike /book — which navigates to /packages because the purchase isn't
    // visible where the customer is — this surface IS the destination. Refresh
    // in place (the hook already calls router.refresh) and keep them on the tab.
    onWalletSuccess: useCallback(
      (balance: number) => onWalletBalanceChange(balance),
      [onWalletBalanceChange],
    ),
  });

  // A completed embedded payment lands the customer on their package list.
  const handleProviderSuccess = useCallback(() => {
    setHyperpay(null);
    setMoyasar(null);
    setStripeSecret(null);
    router.refresh();
  }, [router]);

  // Moyasar calls this before 3DS; let its SDK navigate without a competing refresh.
  const handleMoyasarPaymentCreated = useCallback(() => {
    setMoyasar(null);
  }, []);

  return (
    <>
      <PackageCheckoutDialog
        isOpen={pkg !== null}
        onClose={onClose}
        packageData={
          pkg
            ? {
                id: pkg.id,
                name: pkg.name,
                price: pkg.price,
                booking_count: pkg.booking_count,
                validity_months: pkg.validity_months,
                validity_days: pkg.validity_days,
                slot_duration: pkg.slot_duration,
                original_price: pkg.original_price,
                description: pkg.description,
                features: pkg.features,
                package_type: pkg.package_type,
                color: pkg.color,
                player_pricing: pkg.player_pricing,
                is_bundle: pkg.is_bundle,
                is_fixed_slot: pkg.is_fixed_slot,
                require_attendance_within_validity:
                  pkg.require_attendance_within_validity,
                fixed_starts_at: pkg.fixed_starts_at,
                fixed_ends_at: pkg.fixed_ends_at,
              }
            : null
        }
        onCheckout={purchase}
        onWalletCheckout={purchaseWithWallet}
        isProcessing={isProcessing}
        isAuthenticated={!!userId}
        userId={userId}
        userName={userName}
        userPhone={userPhone}
        tenantId={tenantId}
        tenantName={tenantSlug ?? undefined}
        publishableKey={publishableKey}
        provider={provider}
        locationId={pkg?.location_id ?? undefined}
        publicDiscountCodes={publicDiscountCodes}
        walletEnabled={walletEnabled}
        walletBalance={walletBalance}
      />

      <HyperpayCheckoutDialog
        isOpen={hyperpay !== null}
        onClose={() => setHyperpay(null)}
        checkoutData={hyperpay}
        returnPath={hyperpay?.returnPath}
        onSuccess={handleProviderSuccess}
      />

      <MoyasarCheckoutDialog
        isOpen={moyasar !== null}
        onClose={() => setMoyasar(null)}
        checkoutData={moyasar}
        bookingDetails={{
          amount: moyasar?.packageData?.price ?? 0,
          // NOT display copy — MoyasarCheckoutDialog forwards this into
          // paymentConfig.description, which is stored on the payment and shown
          // in the admin Payments list. It must stay English and un-translated:
          // a t() value would record whatever language the payer's browser
          // happened to be in, making the ledger column unfilterable.
          description: buildPaymentDescription({
            surface: 'package_purchase',
            name: moyasar?.packageData?.name,
          }),
        }}
        // No returnUrl override on purpose. MoyasarCheckoutDialog builds
        // `/book/return?type=package&purchase_id=…&package_id=…` itself from
        // checkoutData.purchaseId, and `returnUrl` takes precedence over it —
        // so passing a bare `?type=package` here would DROP both ids. The
        // return page needs them: without package_id it falls back to a
        // payment_transactions metadata lookup that throws when the webhook
        // has not landed yet, which is exactly the 3DS-return race.
        onSuccess={handleMoyasarPaymentCreated}
      />

      <StripeCheckoutForm
        isOpen={stripeSecret !== null}
        onClose={() => setStripeSecret(null)}
        clientSecret={stripeSecret}
        publishableKey={publishableKey ?? undefined}
      />
    </>
  );
}
